import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Link,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useChat } from './ChatContext';
import { ChatMessage } from '../../types/chat';
import dataDrivenLogo from '../../assets/images/logo/Logo_DataDriven.png';
import alioLogo from '../../assets/images/alioforlogo.png';
import UndoIcon from '../../assets/images/majesticons/undo.svg';
import RocketIcon from '../../assets/images/majesticons/rocket-3-start.svg';
import ChatIcon from '../../assets/images/majesticons/chat-2.svg';

// Message Content Component for rendering links
const MessageContent: React.FC<{ content: string }> = ({ content }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = content.split(urlRegex);

  return (
    <Typography 
      variant="body2" 
      sx={{ 
        whiteSpace: 'pre-wrap',
        color: 'white', // Ensure text is white for visibility
        fontSize: '0.875rem',
        lineHeight: 1.4,
        fontWeight: 400, // Ensure normal font weight for readability
        textShadow: 'none', // Remove any text shadow that might affect visibility
      }}
    >
      {parts.map((part, index) => {
        if (part.match(urlRegex)) {
          const isCalendly = part.includes('calendly.com');
          return (
            <Link
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: isCalendly ? '#FF6B35' : '#4FC3F7',
                textDecoration: 'underline',
                fontWeight: isCalendly ? 600 : 400,
                '&:hover': {
                  color: isCalendly ? '#E55A2B' : '#29B6F6',
                },
              }}
            >
              {part}
            </Link>
          );
        }
        return part;
      })}
    </Typography>
  );
};

const ChatWidget: React.FC = () => {
  const { state, sendMessage, toggleChat, clearChat } = useChat();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [state.messages]);

  // Handle mobile viewport height changes when keyboard appears/disappears
  useEffect(() => {
    if (!isMobile) return;

    const handleViewportChange = () => {
      // Force a re-render when viewport height changes
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set initial viewport height
    handleViewportChange();

    // Listen for viewport changes
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, [isMobile]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+K or Cmd+K to toggle chat
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        toggleChat();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleChat]);

  const handleSendMessage = async () => {
    if (inputValue.trim()) {
      await sendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
    const isUser = message.role === 'user';

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
          gap: 1,
        }}
      >
        {!isUser && (
          <Box
            component="img"
            src={RocketIcon}
            alt="Alio Assistant"
            sx={{
              width: 24,
              height: 24,
              filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(330deg) brightness(118%) contrast(119%)',
              opacity: 0.9,
            }}
          />
        )}
        <Box
          sx={{
            maxWidth: '85%',
            backgroundColor: isUser ? '#FF6B35' : 'rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            p: 1.5,
            position: 'relative',
          }}
        >
          <MessageContent content={message.content} />
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              opacity: 0.7,
              fontSize: '0.7rem',
              color: 'white', // Ensure timestamp is white for visibility
            }}
          >
            {formatTime(message.timestamp)}
          </Typography>
        </Box>
        {isUser && (
          <Avatar
            sx={{
              width: 24,
              height: 24,
              bgcolor: '#FF6B35',
              fontSize: '0.75rem',
            }}
          >
            <PersonIcon sx={{ fontSize: '0.875rem' }} />
          </Avatar>
        )}
      </Box>
    );
  };

  // Don't render anything when chat is closed - it's now triggered from navigation
  if (!state.isOpen) {
    return null;
  }

  // Gemini-style left sidebar panel
  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: isMobile ? '100%' : 320,
        height: isMobile ? 'calc(var(--vh, 1vh) * 100)' : '100vh', // Use custom viewport height on mobile
        backgroundColor: '#121212',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.3)',
        // Additional mobile-specific adjustments
        ...(isMobile && {
          maxHeight: '100vh',
          // Ensure chat doesn't overlap with HubSpot widget
          right: '80px', // Leave space for HubSpot chat widget
          width: 'calc(100% - 80px)', // Adjust width to avoid overlap
        }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src={dataDrivenLogo}
            alt="Alio Logo"
            sx={{
              height: 24,
              width: 'auto',
              objectFit: 'contain',
            }}
          />
          <Typography variant="h6" sx={{ ml: 1, fontSize: '1rem' }}>
            Alio Assistant
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={clearChat}
            sx={{ color: 'white' }}
            title="Clear chat"
          >
            <Box
              component="img"
              src={UndoIcon}
              alt="Clear chat"
              sx={{
                width: 16,
                height: 16,
                filter: 'brightness(0) invert(1)',
              }}
            />
          </IconButton>
          <IconButton
            size="small"
            onClick={toggleChat}
            sx={{ color: 'white' }}
            title="Close chat"
          >
            <CloseIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Messages Body */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          // Mobile-specific improvements
          ...(isMobile && {
            paddingBottom: '1rem', // Extra padding to ensure content isn't hidden behind input
            minHeight: 0, // Allow flex shrinking
            // Ensure smooth scrolling on mobile
            WebkitOverflowScrolling: 'touch',
            // Prevent content from being cut off
            '&::-webkit-scrollbar': {
              width: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '2px',
            },
          }),
        }}
      >
        {state.messages.length === 0 && !state.isLoading ? (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              color: theme.palette.text.secondary,
            }}
          >
            <Box
              component="img"
              src={alioLogo}
              alt="Alio Logo"
              sx={{
                height: 40,
                width: 'auto',
                mb: 2,
                opacity: 0.9,
                objectFit: 'contain',
              }}
            />
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', color: 'white' }}>
              Welcome to Alio!
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)' }}>
              Ask me about our services, white papers, case studies, or how we can help your business.
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)' }}>
              💡 Tip: Press Ctrl+K (or Cmd+K) to quickly open this chat anytime
            </Typography>
          </Box>
        ) : (
          state.messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))
        )}

        {/* Loading indicator */}
        {state.isLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 2,
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={RocketIcon}
              alt="Alio Assistant"
              sx={{
                width: 24,
                height: 24,
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(330deg) brightness(118%) contrast(119%)',
                opacity: 0.9,
              }}
            />
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={14} />
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem', color: 'white' }}>
                Thinking...
              </Typography>
            </Box>
          </Box>
        )}

        {state.challenge && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mb: 2,
              gap: 1,
            }}
          >
            <Box
              component="img"
              src={RocketIcon}
              alt="Alio Assistant"
              sx={{
                width: 24,
                height: 24,
                filter: 'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(330deg) brightness(118%) contrast(119%)',
                opacity: 0.9,
              }}
            />
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxWidth: '85%',
              }}
            >
              <Typography variant="body2" sx={{ color: 'white', fontSize: '0.875rem' }}>
                {state.challenge.question}
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter your answer..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF6B35',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'white',
                    '&::placeholder': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      opacity: 1,
                    },
                  },
                }}
              />
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || state.isLoading}
                sx={{
                  backgroundColor: '#FF6B35',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#E55A2B',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 107, 53, 0.5)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Error Alert */}
      {state.error && (
        <Alert severity="error" sx={{ m: 2, fontSize: '0.875rem' }}>
          {state.error}
        </Alert>
      )}

      {/* HubSpot Redirect Alert */}
      {state.hubSpotRedirect && (
        <Alert 
          severity="info" 
          sx={{ 
            m: 2, 
            fontSize: '0.875rem',
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            border: '1px solid rgba(25, 118, 210, 0.3)',
            '& .MuiAlert-icon': {
              color: '#1976d2',
            },
            '& .MuiAlert-message': {
              color: 'white',
            }
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, mb: 0.5 }}>
              🤖 AI Assistant Limit Reached
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.8rem' }}>
              {state.hubSpotRedirect.message}
            </Typography>
            {state.hubSpotRedirect.retryAfter && (
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem', display: 'block', mt: 0.5 }}>
                ⏱️ You can try again in {Math.ceil(state.hubSpotRedirect.retryAfter / 60)} minutes
              </Typography>
            )}
          </Box>
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
              💬 <strong>Tip:</strong> Look for the chat widget in the bottom right corner of the page for immediate human assistance.
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Input */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: '#121212',
          // Ensure input area is always visible on mobile and doesn't overlap with HubSpot
          ...(isMobile && {
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            paddingBottom: 'calc(2px + env(safe-area-inset-bottom, 0px))',
            // Add right margin to avoid HubSpot chat widget overlap
            marginRight: '80px', // Space for HubSpot chat widget
            // Ensure the input area doesn't extend too far right
            maxWidth: 'calc(100% - 80px)',
          }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={isMobile ? 2 : 3} // Reduce max rows on mobile
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about Alio's services..."
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                fontSize: isMobile ? '1rem' : '0.875rem', // Larger font on mobile for better touch targets
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiInputBase-input': {
                color: 'white',
                fontSize: isMobile ? '1rem' : '0.875rem',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
                },
              },
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || state.isLoading}
            sx={{
              backgroundColor: '#FF6B35',
              color: 'white',
              width: isMobile ? 44 : 36, // Larger touch target on mobile
              height: isMobile ? 44 : 36,
              '&:hover': {
                backgroundColor: '#E55A2B',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            <SendIcon sx={{ fontSize: isMobile ? '1.25rem' : '1rem' }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatWidget; 